'use client';

import React from 'react';
import { FileText } from 'lucide-react';
import PolicyLayout, {
  PolicySection,
  PolicySubSection,
  PolicyList,
  PolicyCallout,
  TocSection,
} from '@/components/PolicyLayout';

const sections: TocSection[] = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'platform-purpose', title: 'Platform Purpose' },
  { id: 'beta-status', title: 'Beta Status' },
  { id: 'ai-limitations', title: 'AI Limitations' },
  { id: 'user-responsibilities', title: 'User Responsibilities' },
  { id: 'prohibited-use', title: 'Prohibited Use' },
  { id: 'automation-limits', title: 'Automation Limitations' },
  { id: 'account-suspension', title: 'Account Suspension' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'no-guarantee', title: 'No Employment Guarantee' },
  { id: 'service-interruption', title: 'Service Interruption' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'contact', title: 'Contact' },
];

export default function TermsClient() {
  return (
    <PolicyLayout
      title="Terms & Conditions"
      description="These terms govern your use of JobCompanion, an AI-assisted job search and application platform currently in controlled beta."
      lastUpdated="May 18, 2026"
      icon={FileText}
      iconBg="bg-accent/10"
      iconColor="text-accent"
      sections={sections}
      currentPath="/terms-and-conditions"
    >
      <PolicySection id="acceptance" title="Acceptance of Terms">
        <p>
          By creating an account or using JobCompanion (&quot;the Platform&quot;, &quot;we&quot;, &quot;us&quot;), you agree to be bound by these Terms &amp; Conditions. If you do not agree, you must not use the platform.
        </p>
        <p>
          These terms apply to all users of the platform, including beta testers, registered users, and visitors. We reserve the right to update these terms at any time with reasonable notice.
        </p>
      </PolicySection>

      <PolicySection id="platform-purpose" title="Platform Purpose">
        <p>
          JobCompanion is an AI-assisted job search and application management platform. Its purpose is to help job seekers:
        </p>
        <PolicyList items={[
          'Upload and analyze resumes using AI',
          'Discover relevant job opportunities through AI-powered matching',
          'Save and organize job listings for later review',
          'Track manual job applications in one place',
          'Generate AI-assisted cover letters and application drafts for human review',
          'Manage semi-automated application workflows with explicit user approval',
        ]} />
        <PolicyCallout type="info">
          JobCompanion is a tool to assist your job search — not a recruitment agency, employment service, or job placement guarantee. We do not represent employers or guarantee job outcomes.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="beta-status" title="Beta Status">
        <p>
          JobCompanion is currently operating in <strong>controlled beta</strong>. This means:
        </p>
        <PolicyList items={[
          'Features may be incomplete, unstable, or subject to change without notice',
          'Data may be reset or migrated during beta operations',
          'Access may be limited or revoked at our discretion',
          'Some features described in documentation may not yet be available',
          'Service level agreements (SLAs) do not apply during beta',
        ]} />
        <p>
          By participating in the beta, you acknowledge these limitations and agree not to rely on the platform for critical job application decisions without independent verification.
        </p>
      </PolicySection>

      <PolicySection id="ai-limitations" title="AI Limitations">
        <p>
          JobCompanion uses artificial intelligence to assist with resume analysis, job matching, and content generation. You must understand and accept the following limitations:
        </p>
        <PolicyList items={[
          'AI outputs are probabilistic and may contain errors, inaccuracies, or outdated information',
          'AI-generated cover letters and answers are drafts — they require your review and editing before use',
          'Job match scores are estimates based on available data and may not reflect actual employer requirements',
          'Resume analysis may miss context, nuance, or industry-specific signals',
          'AI cannot predict hiring decisions, recruiter preferences, or job market conditions',
          'AI-generated content does not constitute professional career advice',
        ]} />
        <PolicyCallout type="warning">
          You are solely responsible for reviewing all AI-generated content before submitting it to any employer. Submitting inaccurate or misleading information in a job application is your responsibility, not ours.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="user-responsibilities" title="User Responsibilities">
        <p>By using JobCompanion, you agree to:</p>
        <PolicyList items={[
          'Provide accurate information in your profile and resume',
          'Review all AI-generated content before using it in job applications',
          'Use the platform only for legitimate personal job search purposes',
          'Maintain the confidentiality of your account credentials',
          'Notify us promptly of any unauthorized account access',
          'Comply with the terms of service of any third-party job platforms you apply to',
          'Not misrepresent your qualifications or experience in applications',
        ]} />
      </PolicySection>

      <PolicySection id="prohibited-use" title="Prohibited Use">
        <p>You may not use JobCompanion to:</p>
        <PolicyList items={[
          'Submit false, misleading, or fraudulent job applications',
          'Scrape, harvest, or extract job listings or user data from the platform',
          'Attempt to reverse-engineer, decompile, or copy platform functionality',
          'Use the platform on behalf of another person without their explicit consent',
          'Circumvent usage limits, rate limits, or access controls',
          'Upload malicious files, scripts, or content',
          'Engage in any activity that violates applicable laws or regulations',
          'Use the platform for commercial recruitment, lead generation, or data harvesting',
          'Attempt to access other users\' accounts or data',
        ]} />
        <p>Violation of these prohibitions may result in immediate account suspension. See <a href="#account-suspension" className="text-primary underline">Account Suspension</a>.</p>
      </PolicySection>

      <PolicySection id="automation-limits" title="Automation Limitations">
        <p>
          JobCompanion&apos;s automation features (AI-assisted workflows, future auto-apply) are subject to strict limitations:
        </p>
        <PolicyList items={[
          'No automated submission occurs without your explicit review and approval in the current beta',
          'Auto-apply features (when released) require explicit opt-in and configuration',
          'Automation will never solve CAPTCHAs, bypass bot detection, or circumvent employer security measures',
          'Automation will pause and alert you when login walls, payment pages, or unusual forms are detected',
          'Daily and monthly automation limits apply to prevent abuse',
          'We reserve the right to pause or disable automation for any account showing unusual activity',
        ]} />
        <PolicyCallout type="warning">
          Using JobCompanion to automate applications in violation of a job platform&apos;s terms of service is your responsibility. We recommend reviewing the terms of each platform you apply to.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="account-suspension" title="Account Suspension & Termination">
        <p>We reserve the right to suspend or terminate your account if you:</p>
        <PolicyList items={[
          'Violate these Terms & Conditions',
          'Engage in prohibited use as defined above',
          'Provide false information during registration',
          'Attempt to abuse, overload, or attack platform infrastructure',
          'Use the platform for fraudulent job applications',
          'Fail to comply with a legal request or court order',
        ]} />
        <p>
          To exercise any of these rights, you may appeal a suspension by contacting us at <a href="mailto:support@jobcompanion.app" className="text-primary underline">support@jobcompanion.app</a>. We will review appeals within 10 business days.
        </p>
      </PolicySection>

      <PolicySection id="liability" title="Limitation of Liability">
        <p>
          To the maximum extent permitted by applicable law, JobCompanion and its operators shall not be liable for:
        </p>
        <PolicyList items={[
          'Any indirect, incidental, special, or consequential damages',
          'Loss of employment opportunities or income',
          'Errors or inaccuracies in AI-generated content',
          'Data loss due to technical failures (though we take reasonable precautions)',
          'Actions taken by employers based on applications submitted through the platform',
          'Service interruptions, downtime, or beta-related instability',
          'Third-party job platform policies or decisions',
        ]} />
        <p>
          Our total liability to you for any claim arising from use of the platform shall not exceed the amount you paid us in the 12 months preceding the claim (or $0 if you are on a free plan).
        </p>
      </PolicySection>

      <PolicySection id="no-guarantee" title="No Employment Guarantee">
        <p>
          JobCompanion does not guarantee employment, job interviews, recruiter responses, or any specific outcome from using the platform. Job search outcomes depend on many factors outside our control, including:
        </p>
        <PolicyList items={[
          'Employer hiring decisions and internal processes',
          'Job market conditions and competition',
          'Your qualifications relative to other applicants',
          'Accuracy and completeness of your resume and application materials',
          'Timing and availability of job openings',
        ]} />
        <p>
          We are a tool to assist your job search — not a guarantee of results.
        </p>
      </PolicySection>

      <PolicySection id="service-interruption" title="Service Interruption Disclaimer">
        <p>
          JobCompanion is provided &quot;as is&quot; and &quot;as available.&quot; We do not guarantee uninterrupted access to the platform. During beta operations, we may:
        </p>
        <PolicyList items={[
          'Take the platform offline for maintenance without advance notice',
          'Reset or migrate data as part of infrastructure updates',
          'Disable specific features for testing or safety reviews',
          'Limit access to control traffic during controlled beta launch',
        ]} />
        <p>
          We will make reasonable efforts to notify users of planned downtime via email or in-app notices.
        </p>
      </PolicySection>

      <PolicySection id="governing-law" title="Governing Law">
        <p>
          These Terms are governed by the laws of India, without regard to conflict of law principles. Any disputes shall be resolved in the courts of India.
        </p>
      </PolicySection>

      <PolicySection id="contact" title="Contact">
        <p>
          Questions about these Terms? Contact us at <a href="mailto:support@jobcompanion.app" className="text-primary underline">support@jobcompanion.app</a> or visit our <a href="/contact-grievance" className="text-primary underline">Contact &amp; Grievance</a> page.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
